import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { authService } from '../api/axios';
import './TeamChatAdmin.css';

const STALE_HOURS = 4;
const PAGE_SIZE = 20;

function isStale(session) {
    if (session.status !== 'OPEN') return false;
    const activity = session.lastActivityAt || session.createdAt;
    if (!activity) return false;
    const d = new Date(activity.endsWith('Z') || activity.includes('+') ? activity : activity + 'Z');
    return (Date.now() - d.getTime()) > STALE_HOURS * 3600 * 1000;
}

function toUtcDate(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
}

function formatRelative(dateStr) {
    if (!dateStr) return '—';
    const d = toUtcDate(dateStr);
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return `${Math.floor(diffHrs / 24)}d ago`;
}

function shortId(senderId) {
    return senderId?.includes('@') ? senderId.split('@')[0] : senderId;
}

function StatTile({ label, value, color, active, onClick }) {
    return (
        <div className={`tca-tile ${active ? 'active' : ''}`} style={{ '--tile-color': color }} onClick={onClick}>
            <div className="tca-tile-value">{value}</div>
            <div className="tca-tile-label">{label}</div>
        </div>
    );
}

function UserActivityBadge({ userId }) {
    const [activity, setActivity] = useState(null);

    useEffect(() => {
        api.get(`internal-chat/admin/user/${encodeURIComponent(userId)}/activity`)
            .then(r => setActivity(r.data))
            .catch(() => {});
    }, [userId]);

    if (!activity) return null;

    return (
        <div className="tca-user-activity">
            <span className="tca-activity-stat">
                <strong>{activity.sessionsCreated}</strong> asked
            </span>
            <span className="tca-activity-divider">·</span>
            <span className="tca-activity-stat">
                <strong>{activity.totalReplies - activity.sessionsCreated}</strong> replies
            </span>
            <span className="tca-activity-divider">·</span>
            <span className="tca-activity-stat">
                <strong>{activity.sessionsParticipated}</strong> sessions
            </span>
        </div>
    );
}

function MessageView({ sessionId }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setLoading(true);
        setPage(0);
        api.get(`internal-chat/sessions/${sessionId}/messages?page=0&size=50`)
            .then(r => {
                // backend returns DESC, reverse for chronological display
                setMessages([...(r.data.content || [])].reverse());
                setHasMore(!r.data.last);
            })
            .catch(() => setMessages([]))
            .finally(() => setLoading(false));
    }, [sessionId]);

    const loadEarlier = () => {
        const nextPage = page + 1;
        api.get(`internal-chat/sessions/${sessionId}/messages?page=${nextPage}&size=50`)
            .then(r => {
                const older = [...(r.data.content || [])].reverse();
                setMessages(prev => [...older, ...prev]);
                setHasMore(!r.data.last);
                setPage(nextPage);
            })
            .catch(() => {});
    };

    if (loading) return <div className="tca-msg-loading">Loading...</div>;

    return (
        <div className="tca-messages">
            {hasMore && (
                <button className="tca-load-earlier-btn" onClick={loadEarlier}>
                    ↑ Load earlier
                </button>
            )}
            {messages.map(msg => (
                <div key={msg.id} className={`tca-msg ${msg.senderType === 'AI' ? 'ai' : ''}`}>
                    <div className="tca-msg-header">
                        <span className="tca-msg-sender">
                            {msg.senderType === 'AI' ? '🤖 AI Assistant' : shortId(msg.senderId)}
                        </span>
                        <span className="tca-msg-time">{formatRelative(msg.sentAt)}</span>
                    </div>
                    <div className="tca-msg-content">{msg.content}</div>
                </div>
            ))}
        </div>
    );
}

function UserFilter({ value, onChange }) {
    const [members, setMembers] = useState([]);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        authService.get('auth/internal-users')
            .then(r => setMembers(r.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const select = (username) => { onChange(username || null); setOpen(false); };

    return (
        <div className="tca-user-filter" ref={ref}>
            <button
                className={`tca-user-filter-trigger ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                {value ? `@${shortId(value)}` : 'All users'}
                <svg className="tca-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>

            {open && (
                <div className="tca-user-filter-dropdown">
                    <div className={`tca-user-option ${!value ? 'selected' : ''}`} onClick={() => select(null)}>
                        All users
                    </div>
                    {members.map(m => (
                        <div
                            key={m.username}
                            className={`tca-user-option ${value === m.username ? 'selected' : ''}`}
                            onClick={() => select(m.username)}
                        >
                            @{shortId(m.username)}
                            <span className="tca-user-role">{m.role}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function TeamChatAdmin() {
    const [stats, setStats] = useState({ total: 0, open: 0, resolved: 0, stale: 0 });
    const [sessions, setSessions] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState('all');
    const [userFilter, setUserFilter] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const buildUrl = useCallback((p = 0) => {
        if (userFilter) {
            return `internal-chat/admin/user/${encodeURIComponent(userFilter)}/sessions?page=${p}&size=${PAGE_SIZE}`;
        }
        return `internal-chat/admin/sessions?page=${p}&size=${PAGE_SIZE}`;
    }, [userFilter]);

    const load = useCallback(async () => {
        setLoading(true);
        setPage(0);
        setExpandedId(null);
        try {
            const requests = [api.get(buildUrl(0))];
            if (!userFilter) requests.unshift(api.get('internal-chat/admin/stats'));
            const results = await Promise.all(requests);
            if (!userFilter) {
                setStats(results[0].data);
                setSessions(results[1].data.content || []);
                setHasMore(!results[1].data.last);
            } else {
                setSessions(results[0].data.content || []);
                setHasMore(!results[0].data.last);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [buildUrl, userFilter]);

    const loadMore = useCallback(async () => {
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const res = await api.get(buildUrl(nextPage));
            setSessions(prev => [...prev, ...(res.data.content || [])]);
            setHasMore(!res.data.last);
            setPage(nextPage);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingMore(false);
        }
    }, [buildUrl, page]);

    useEffect(() => { load(); }, [load]);

    // Reload stats separately when switching off user filter
    useEffect(() => {
        if (!userFilter) {
            api.get('internal-chat/admin/stats').then(r => setStats(r.data)).catch(() => {});
        }
    }, [userFilter]);

    const handleResolve = async (sessionId, e) => {
        e.stopPropagation();
        await api.post(`internal-chat/sessions/${sessionId}/resolve`);
        load();
    };

    const filtered = sessions.filter(s => {
        if (filter === 'open') return s.status === 'OPEN';
        if (filter === 'resolved') return s.status === 'RESOLVED';
        if (filter === 'stale') return isStale(s);
        return true;
    });

    return (
        <div className="tca-container">
            <div className="tca-header">
                <h2 className="tca-title">Team Chat Overview</h2>
                <div className="tca-header-actions">
                    <UserFilter value={userFilter} onChange={setUserFilter} />
                    <button className="tca-refresh-btn" onClick={load} disabled={loading}>
                        {loading ? 'Loading...' : '↻ Refresh'}
                    </button>
                </div>
            </div>

            {!userFilter && (
                <div className="tca-tiles">
                    <StatTile label="Total Chats"  value={stats.total}    color="#1a73e8" active={filter === 'all'}      onClick={() => setFilter('all')} />
                    <StatTile label="Open"         value={stats.open}     color="#f9a825" active={filter === 'open'}     onClick={() => setFilter('open')} />
                    <StatTile label="Resolved"     value={stats.resolved} color="#43a047" active={filter === 'resolved'} onClick={() => setFilter('resolved')} />
                    <StatTile label="Stale (>4h)"  value={stats.stale}    color="#e53935" active={filter === 'stale'}    onClick={() => setFilter('stale')} />
                </div>
            )}

            {userFilter && (
                <div className="tca-user-view-header">
                    <div>
                        <span className="tca-user-view-name">@{shortId(userFilter)}</span>
                        <span className="tca-user-view-sub">All sessions this user participated in</span>
                    </div>
                    <UserActivityBadge userId={userFilter} />
                </div>
            )}

            {loading ? (
                <div className="tca-loading">Loading chats...</div>
            ) : filtered.length === 0 ? (
                <div className="tca-empty">No chats match this filter.</div>
            ) : (
                <>
                    <div className="tca-session-list">
                        {filtered.map(session => {
                            const stale = isStale(session);
                            const expanded = expandedId === session.id;
                            return (
                                <div key={session.id} className={`tca-session ${stale ? 'stale' : ''}`}>
                                    <div className="tca-session-row" onClick={() => setExpandedId(expanded ? null : session.id)}>
                                        <div className="tca-session-main">
                                            <span className={`tca-status-dot ${session.status.toLowerCase()}`} />
                                            <span className="tca-session-title">{session.title}</span>
                                            {stale && <span className="tca-stale-badge">Stale</span>}
                                            {session.directedTo && (
                                                <span className="tca-directed-badge">→ @{shortId(session.directedTo)}</span>
                                            )}
                                        </div>
                                        <div className="tca-session-meta">
                                            <span>by {shortId(session.createdBy)}</span>
                                            <span>{session.messageCount} msg{session.messageCount !== 1 ? 's' : ''}</span>
                                            <span>active {formatRelative(session.lastActivityAt || session.createdAt)}</span>
                                            {session.status === 'OPEN' && (
                                                <button className="tca-resolve-btn" onClick={e => handleResolve(session.id, e)}>
                                                    Resolve
                                                </button>
                                            )}
                                            <span className="tca-expand-icon">{expanded ? '▲' : '▼'}</span>
                                        </div>
                                    </div>
                                    {expanded && <MessageView sessionId={session.id} />}
                                </div>
                            );
                        })}
                    </div>

                    {hasMore && (
                        <button className="tca-load-more-btn" onClick={loadMore} disabled={loadingMore}>
                            {loadingMore ? 'Loading...' : 'Load more sessions'}
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
